import type { Session } from '@supabase/supabase-js';

/**
 * Lemon Squeezy checkout (Merchant of Record). We don't process cards or hold a
 * payment secret in the browser — we just send the user to a hosted checkout,
 * tagging it with their Supabase user id. When Lemon Squeezy confirms the
 * payment it calls our webhook (supabase/functions/lemonsqueezy-webhook), which
 * flips that user's `app_metadata.plan` to 'pro'. See [[freemium-model]].
 *
 * Set VITE_LEMONSQUEEZY_CHECKOUT_URL to the product's "buy link"
 * (e.g. https://<store>.lemonsqueezy.com/buy/<uuid>). Until it's set, the
 * upgrade UI stays hidden.
 */
const checkoutBase = import.meta.env.VITE_LEMONSQUEEZY_CHECKOUT_URL as string | undefined;

export const isBillingConfigured = Boolean(checkoutBase);

/** Pro price, shown on the landing + upgrade panel. Keep in sync with the Lemon
 *  Squeezy product; override via env without touching code if it changes.
 *  NOTE: if the price changes, also update the hardcoded "$9"/"9" in
 *  index.html's SoftwareApplication + FAQPage JSON-LD — Google requires the
 *  structured data to match the visible page text. */
export const PRO_PRICE = (import.meta.env.VITE_PRO_PRICE as string | undefined) || '$9';
export const PRO_PERIOD = 'month';

/** Lemon Squeezy customer portal, where a subscriber manages/cancels their
 *  plan. Defaults to the universal "my orders" page (email magic-link login). */
const portalUrl =
  (import.meta.env.VITE_LEMONSQUEEZY_PORTAL_URL as string | undefined) ||
  'https://app.lemonsqueezy.com/my-orders';

export function openBillingPortal(): void {
  window.open(portalUrl, '_blank', 'noopener,noreferrer');
}

/** Build the checkout URL, tagging it with the user id (echoed back in the
 *  webhook as `meta.custom_data.user_id`) and prefilling their email. */
export function buildCheckoutUrl(session: Session | null): string | null {
  if (!checkoutBase) return null;
  const url = new URL(checkoutBase);
  const uid = session?.user?.id;
  const email = session?.user?.email;
  // Lemon Squeezy reads these bracketed params on its hosted checkout.
  if (uid) url.searchParams.set('checkout[custom][user_id]', uid);
  if (email) url.searchParams.set('checkout[email]', email);
  return url.toString();
}

/** Open the hosted checkout in a new tab. */
export function openCheckout(session: Session | null): void {
  const url = buildCheckoutUrl(session);
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}
